import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getCostCenterTypes, getCCFinancialYear, getCostCenterSubTypes, getMasterWorkFlowStatus, getCostCenters,
    getCCDetails, saveCCAssignedBudget, saveCCAmendBudget, getBudgetAssignedCCByID, getOtherBudgetCCs,
    getBudgetAssignedCCByCCType, getDCABudgetDetails, getCCDetailsbyID, getBudgetDCADetails, getBudgetCostCenters,
    saveDCAAssignedBudget, saveSingleDCAAmendBudget, getAmendedDCA, getUnAmendedDCAByCCID,
    assignDCABudgetFromAmendment, multipleDCAAmend, deleteDCAAmendBudget,
} from '../../api/BudgetAPI/assignBudgetAPI';

// API failures come back as { Message: 'API Execution Failed', ErrorDetails: { ErrorMessage } }: show the real reason
const err = (e, fallback) => (typeof e === 'string' ? e : null) || e?.ErrorDetails?.ErrorMessage || e?.Message || e?.message || fallback;

// ── Shared lookups ───────────────────────────────────────────────────────────

export const fetchCCTypes = createAsyncThunk('assignBudget/fetchCCTypes', async (_, { rejectWithValue }) => {
    try { const res = await getCostCenterTypes(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch CC types')); }
});
export const fetchCCFinancialYears = createAsyncThunk('assignBudget/fetchYears', async (_, { rejectWithValue }) => {
    try { const res = await getCCFinancialYear(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch years')); }
});
export const fetchCCSubTypes = createAsyncThunk('assignBudget/fetchSubTypes', async (_, { rejectWithValue }) => {
    try { const res = await getCostCenterSubTypes(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch sub types')); }
});
export const fetchWorkflowStatus = createAsyncThunk('assignBudget/fetchWorkflow', async (roleId, { rejectWithValue }) => {
    try { const res = await getMasterWorkFlowStatus('134,136,140,142', roleId); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch workflow status')); }
});
export const fetchCostCentersForAssign = createAsyncThunk('assignBudget/fetchCostCenters', async (params, { rejectWithValue }) => {
    try { const res = await getCostCenters(params); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch cost centres')); }
});

// ── CC Budget ────────────────────────────────────────────────────────────────

export const fetchCCGrid = createAsyncThunk('assignBudget/fetchCCGrid', async (params, { rejectWithValue }) => {
    try { const res = await getCCDetails(params); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch CC budget grid')); }
});
export const submitAssignCCBudget = createAsyncThunk('assignBudget/submitAssignCC', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveCCAssignedBudget(payload);
        const status = res?.Data;
        const ok = status && (status.split(',')[0] === 'Submited' || status.split(',')[0] === 'Submitted');
        if (!ok) return rejectWithValue(status || 'Failed to save budget');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save budget')); }
});
export const submitAmendCCBudget = createAsyncThunk('assignBudget/submitAmendCC', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveCCAmendBudget(payload);
        const status = res?.Data;
        const ok = status && (status.split('$')[0] === 'Submited' || status.split('$')[0] === 'Submitted');
        if (!ok) return rejectWithValue(status || 'Failed to save amendment');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save amendment')); }
});
export const fetchCCBalance = createAsyncThunk('assignBudget/fetchCCBalance', async (params, { rejectWithValue }) => {
    try { const res = await getBudgetAssignedCCByID(params); return res?.Data?.[0] || null; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch CC balance')); }
});
export const fetchOtherCCs = createAsyncThunk('assignBudget/fetchOtherCCs', async (params, { rejectWithValue }) => {
    try { const res = await getOtherBudgetCCs(params); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch other cost centres')); }
});

// ── DCA Budget ───────────────────────────────────────────────────────────────

export const fetchCCsForDCA = createAsyncThunk('assignBudget/fetchCCsForDCA', async (params, { rejectWithValue }) => {
    try { const res = await getBudgetAssignedCCByCCType(params); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch cost centres')); }
});
export const fetchDCAGrid = createAsyncThunk('assignBudget/fetchDCAGrid', async (params, { rejectWithValue }) => {
    try { const res = await getDCABudgetDetails(params); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch DCA budget grid')); }
});
export const fetchBudgetCostCenters = createAsyncThunk('assignBudget/fetchBudgetCostCenters', async (_, { rejectWithValue }) => {
    try { const res = await getBudgetCostCenters(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch cost centres')); }
});
export const fetchCCDetailsById = createAsyncThunk('assignBudget/fetchCCDetailsById', async (ccId, { rejectWithValue }) => {
    try { const res = await getCCDetailsbyID(ccId); return res?.Data?.[0] || null; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch cost centre details')); }
});
export const fetchBudgetDCADetails = createAsyncThunk('assignBudget/fetchBudgetDCADetails', async (ccTypeId, { rejectWithValue }) => {
    try { const res = await getBudgetDCADetails(ccTypeId); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch account heads')); }
});
export const submitAssignDCABudget = createAsyncThunk('assignBudget/submitAssignDCA', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveDCAAssignedBudget(payload);
        const status = res?.Data;
        if (status !== 'Submited' && status !== 'Submitted') return rejectWithValue(status || 'Failed to save account head budget');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save account head budget')); }
});
export const submitSingleDCAAmend = createAsyncThunk('assignBudget/submitSingleDCAAmend', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveSingleDCAAmendBudget(payload);
        const status = res?.Data;
        if (status !== 'Submited' && status !== 'Submitted') return rejectWithValue(status || 'Failed to stage amendment');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to stage amendment')); }
});
export const fetchPendingDCAAmendments = createAsyncThunk('assignBudget/fetchPendingAmendments', async (ccCode, { rejectWithValue }) => {
    try { const res = await getAmendedDCA(ccCode); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch pending amendments')); }
});
export const deleteSingleDCAAmendment = createAsyncThunk('assignBudget/deleteSingleDCAAmendment', async (payload, { rejectWithValue }) => {
    try {
        const res = await deleteDCAAmendBudget(payload);
        if (!res?.Data) return rejectWithValue('Failed to remove staged amendment');
        return payload.DCABudgetAmendmentId;
    } catch (e) { return rejectWithValue(err(e, 'Failed to remove staged amendment')); }
});
export const fetchUnAmendedDCAs = createAsyncThunk('assignBudget/fetchUnAmendedDCAs', async (params, { rejectWithValue }) => {
    try { const res = await getUnAmendedDCAByCCID(params); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch unbudgeted account heads')); }
});
export const submitNewDCAUnderCC = createAsyncThunk('assignBudget/submitNewDCAUnderCC', async (payload, { rejectWithValue }) => {
    try {
        const res = await assignDCABudgetFromAmendment(payload);
        const status = res?.Data;
        if (status !== 'Submited' && status !== 'Submitted') return rejectWithValue(status || 'Failed to add account head budget');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to add account head budget')); }
});
export const submitMultipleDCAAmend = createAsyncThunk('assignBudget/submitMultipleDCAAmend', async (payload, { rejectWithValue }) => {
    try {
        const res = await multipleDCAAmend(payload);
        const status = res?.Data;
        if (status !== 'Submited' && status !== 'Submitted') return rejectWithValue(status || 'Failed to confirm amendments');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to confirm amendments')); }
});

const initialState = {
    ccTypes: [], ccTypesLoading: false,
    ccFinancialYears: [], ccFinancialYearsLoading: false,
    ccSubTypes: [], ccSubTypesLoading: false,
    workflowStatus: [], workflowStatusLoading: false,
    costCentersForAssign: [], costCentersForAssignLoading: false,

    ccGrid: [], ccGridLoading: false, ccGridError: null,
    ccSaveLoading: false, ccSaveResult: null, ccSaveError: null,
    ccBalance: null, ccBalanceLoading: false,
    otherCCs: [], otherCCsLoading: false,

    ccsForDCA: [], ccsForDCALoading: false,
    dcaGrid: [], dcaGridLoading: false, dcaGridError: null,
    budgetCostCenters: [], budgetCostCentersLoading: false,
    ccDetailsById: null, ccDetailsByIdLoading: false,
    budgetDCADetails: [], budgetDCADetailsLoading: false,
    dcaSaveLoading: false, dcaSaveResult: null, dcaSaveError: null,
    singleAmendLoading: false, singleAmendResult: null, singleAmendError: null,
    pendingAmendments: [], pendingAmendmentsLoading: false,
    unAmendedDCAs: [], unAmendedDCAsLoading: false,
    newDCALoading: false, newDCAResult: null, newDCAError: null,
    confirmAmendLoading: false, confirmAmendResult: null, confirmAmendError: null,
};

const assignBudgetSlice = createSlice({
    name: 'assignBudget',
    initialState,
    reducers: {
        clearCCSaveResult: (s) => { s.ccSaveResult = null; s.ccSaveError = null; },
        clearDCASaveResult: (s) => { s.dcaSaveResult = null; s.dcaSaveError = null; },
        clearSingleAmendResult: (s) => { s.singleAmendResult = null; s.singleAmendError = null; },
        clearNewDCAResult: (s) => { s.newDCAResult = null; s.newDCAError = null; },
        clearConfirmAmendResult: (s) => { s.confirmAmendResult = null; s.confirmAmendError = null; },
        clearCCBalance: (s) => { s.ccBalance = null; },
        clearCCDetailsById: (s) => { s.ccDetailsById = null; s.budgetDCADetails = []; },
        resetAssignBudget: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCCTypes.pending, (s) => { s.ccTypesLoading = true; })
            .addCase(fetchCCTypes.fulfilled, (s, a) => { s.ccTypesLoading = false; s.ccTypes = a.payload; })
            .addCase(fetchCCTypes.rejected, (s) => { s.ccTypesLoading = false; });

        builder
            .addCase(fetchCCFinancialYears.pending, (s) => { s.ccFinancialYearsLoading = true; })
            .addCase(fetchCCFinancialYears.fulfilled, (s, a) => { s.ccFinancialYearsLoading = false; s.ccFinancialYears = a.payload; })
            .addCase(fetchCCFinancialYears.rejected, (s) => { s.ccFinancialYearsLoading = false; });

        builder
            .addCase(fetchCCSubTypes.pending, (s) => { s.ccSubTypesLoading = true; })
            .addCase(fetchCCSubTypes.fulfilled, (s, a) => { s.ccSubTypesLoading = false; s.ccSubTypes = a.payload; })
            .addCase(fetchCCSubTypes.rejected, (s) => { s.ccSubTypesLoading = false; });

        builder
            .addCase(fetchWorkflowStatus.pending, (s) => { s.workflowStatusLoading = true; })
            .addCase(fetchWorkflowStatus.fulfilled, (s, a) => { s.workflowStatusLoading = false; s.workflowStatus = a.payload; })
            .addCase(fetchWorkflowStatus.rejected, (s) => { s.workflowStatusLoading = false; });

        builder
            .addCase(fetchCostCentersForAssign.pending, (s) => { s.costCentersForAssignLoading = true; })
            .addCase(fetchCostCentersForAssign.fulfilled, (s, a) => { s.costCentersForAssignLoading = false; s.costCentersForAssign = a.payload; })
            .addCase(fetchCostCentersForAssign.rejected, (s) => { s.costCentersForAssignLoading = false; });

        builder
            .addCase(fetchCCGrid.pending, (s) => { s.ccGridLoading = true; s.ccGridError = null; })
            .addCase(fetchCCGrid.fulfilled, (s, a) => { s.ccGridLoading = false; s.ccGrid = a.payload; })
            .addCase(fetchCCGrid.rejected, (s, a) => { s.ccGridLoading = false; s.ccGridError = a.payload; });

        builder
            .addCase(submitAssignCCBudget.pending, (s) => { s.ccSaveLoading = true; s.ccSaveError = null; s.ccSaveResult = null; })
            .addCase(submitAssignCCBudget.fulfilled, (s, a) => { s.ccSaveLoading = false; s.ccSaveResult = a.payload; })
            .addCase(submitAssignCCBudget.rejected, (s, a) => { s.ccSaveLoading = false; s.ccSaveError = a.payload; });

        builder
            .addCase(submitAmendCCBudget.pending, (s) => { s.ccSaveLoading = true; s.ccSaveError = null; s.ccSaveResult = null; })
            .addCase(submitAmendCCBudget.fulfilled, (s, a) => { s.ccSaveLoading = false; s.ccSaveResult = a.payload; })
            .addCase(submitAmendCCBudget.rejected, (s, a) => { s.ccSaveLoading = false; s.ccSaveError = a.payload; });

        builder
            .addCase(fetchCCBalance.pending, (s) => { s.ccBalanceLoading = true; })
            .addCase(fetchCCBalance.fulfilled, (s, a) => { s.ccBalanceLoading = false; s.ccBalance = a.payload; })
            .addCase(fetchCCBalance.rejected, (s) => { s.ccBalanceLoading = false; });

        builder
            .addCase(fetchOtherCCs.pending, (s) => { s.otherCCsLoading = true; })
            .addCase(fetchOtherCCs.fulfilled, (s, a) => { s.otherCCsLoading = false; s.otherCCs = a.payload; })
            .addCase(fetchOtherCCs.rejected, (s) => { s.otherCCsLoading = false; });

        builder
            .addCase(fetchCCsForDCA.pending, (s) => { s.ccsForDCALoading = true; })
            .addCase(fetchCCsForDCA.fulfilled, (s, a) => { s.ccsForDCALoading = false; s.ccsForDCA = a.payload; })
            .addCase(fetchCCsForDCA.rejected, (s) => { s.ccsForDCALoading = false; });

        builder
            .addCase(fetchDCAGrid.pending, (s) => { s.dcaGridLoading = true; s.dcaGridError = null; })
            .addCase(fetchDCAGrid.fulfilled, (s, a) => { s.dcaGridLoading = false; s.dcaGrid = a.payload; })
            .addCase(fetchDCAGrid.rejected, (s, a) => { s.dcaGridLoading = false; s.dcaGridError = a.payload; });

        builder
            .addCase(fetchBudgetCostCenters.pending, (s) => { s.budgetCostCentersLoading = true; })
            .addCase(fetchBudgetCostCenters.fulfilled, (s, a) => { s.budgetCostCentersLoading = false; s.budgetCostCenters = a.payload; })
            .addCase(fetchBudgetCostCenters.rejected, (s) => { s.budgetCostCentersLoading = false; s.budgetCostCenters = []; });

        builder
            .addCase(fetchCCDetailsById.pending, (s) => { s.ccDetailsByIdLoading = true; })
            .addCase(fetchCCDetailsById.fulfilled, (s, a) => { s.ccDetailsByIdLoading = false; s.ccDetailsById = a.payload; })
            .addCase(fetchCCDetailsById.rejected, (s) => { s.ccDetailsByIdLoading = false; });

        builder
            .addCase(fetchBudgetDCADetails.pending, (s) => { s.budgetDCADetailsLoading = true; })
            .addCase(fetchBudgetDCADetails.fulfilled, (s, a) => { s.budgetDCADetailsLoading = false; s.budgetDCADetails = a.payload; })
            .addCase(fetchBudgetDCADetails.rejected, (s) => { s.budgetDCADetailsLoading = false; });

        builder
            .addCase(submitAssignDCABudget.pending, (s) => { s.dcaSaveLoading = true; s.dcaSaveError = null; s.dcaSaveResult = null; })
            .addCase(submitAssignDCABudget.fulfilled, (s, a) => { s.dcaSaveLoading = false; s.dcaSaveResult = a.payload; })
            .addCase(submitAssignDCABudget.rejected, (s, a) => { s.dcaSaveLoading = false; s.dcaSaveError = a.payload; });

        builder
            .addCase(submitSingleDCAAmend.pending, (s) => { s.singleAmendLoading = true; s.singleAmendError = null; s.singleAmendResult = null; })
            .addCase(submitSingleDCAAmend.fulfilled, (s, a) => { s.singleAmendLoading = false; s.singleAmendResult = a.payload; })
            .addCase(submitSingleDCAAmend.rejected, (s, a) => { s.singleAmendLoading = false; s.singleAmendError = a.payload; });

        builder
            .addCase(fetchPendingDCAAmendments.pending, (s) => { s.pendingAmendmentsLoading = true; })
            .addCase(fetchPendingDCAAmendments.fulfilled, (s, a) => { s.pendingAmendmentsLoading = false; s.pendingAmendments = a.payload; })
            .addCase(fetchPendingDCAAmendments.rejected, (s) => { s.pendingAmendmentsLoading = false; });

        builder
            .addCase(deleteSingleDCAAmendment.fulfilled, (s, a) => {
                s.pendingAmendments = s.pendingAmendments.filter((p) => p.PendingTransactionId !== a.payload);
            });

        builder
            .addCase(fetchUnAmendedDCAs.pending, (s) => { s.unAmendedDCAsLoading = true; })
            .addCase(fetchUnAmendedDCAs.fulfilled, (s, a) => { s.unAmendedDCAsLoading = false; s.unAmendedDCAs = a.payload; })
            .addCase(fetchUnAmendedDCAs.rejected, (s) => { s.unAmendedDCAsLoading = false; });

        builder
            .addCase(submitNewDCAUnderCC.pending, (s) => { s.newDCALoading = true; s.newDCAError = null; s.newDCAResult = null; })
            .addCase(submitNewDCAUnderCC.fulfilled, (s, a) => { s.newDCALoading = false; s.newDCAResult = a.payload; })
            .addCase(submitNewDCAUnderCC.rejected, (s, a) => { s.newDCALoading = false; s.newDCAError = a.payload; });

        builder
            .addCase(submitMultipleDCAAmend.pending, (s) => { s.confirmAmendLoading = true; s.confirmAmendError = null; s.confirmAmendResult = null; })
            .addCase(submitMultipleDCAAmend.fulfilled, (s, a) => { s.confirmAmendLoading = false; s.confirmAmendResult = a.payload; })
            .addCase(submitMultipleDCAAmend.rejected, (s, a) => { s.confirmAmendLoading = false; s.confirmAmendError = a.payload; });
    },
});

export const {
    clearCCSaveResult, clearDCASaveResult, clearSingleAmendResult, clearNewDCAResult,
    clearConfirmAmendResult, clearCCBalance, clearCCDetailsById, resetAssignBudget,
} = assignBudgetSlice.actions;

export const selectAssignBudget = (s) => s.assignBudget;

export default assignBudgetSlice.reducer;
